/**
 * Complete chess rules engine.
 *
 * Board is a flat 64-entry array, index 0 = a8 ... index 63 = h1, matching the
 * way FEN is written. Squares hold either null or a piece object
 * `{ type: 'p'|'n'|'b'|'r'|'q'|'k', color: 'w'|'b', id: string }`.
 * Piece ids are stable for the lifetime of a piece so the renderer can track
 * meshes across moves.
 */

export const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
export const START_FEN =
  'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

export const idx = (file, rank) => (8 - rank) * 8 + file; // rank 1..8, file 0..7
export const fileOf = (i) => i % 8;
export const rankOf = (i) => 8 - Math.floor(i / 8);
export const squareName = (i) => FILES[fileOf(i)] + rankOf(i);
export const onBoard = (f, r) => f >= 0 && f < 8 && r >= 1 && r <= 8;
export const other = (c) => (c === 'w' ? 'b' : 'w');

const KNIGHT_DELTAS = [
  [1, 2], [2, 1], [2, -1], [1, -2],
  [-1, -2], [-2, -1], [-2, 1], [-1, 2]
];
const KING_DELTAS = [
  [1, 0], [1, 1], [0, 1], [-1, 1],
  [-1, 0], [-1, -1], [0, -1], [1, -1]
];
const ROOK_DIRS = [[1, 0], [-1, 0], [0, 1], [0, -1]];
const BISHOP_DIRS = [[1, 1], [1, -1], [-1, 1], [-1, -1]];

let pieceSeq = 0;
const newId = (type, color) => `${color}${type}${++pieceSeq}`;

export class Chess {
  constructor(fen = START_FEN) {
    this.load(fen);
  }

  load(fen) {
    const [placement, turn, castling, ep, half, full] = fen.trim().split(/\s+/);
    this.board = new Array(64).fill(null);

    let i = 0;
    for (const ch of placement) {
      if (ch === '/') continue;
      if (/\d/.test(ch)) {
        i += Number(ch);
        continue;
      }
      const color = ch === ch.toUpperCase() ? 'w' : 'b';
      const type = ch.toLowerCase();
      // `origin` is the square a piece was first seen on, and unlike `id` it
      // survives a replay: `newId`'s counter never resets, so rebuilding the
      // board after an undo issues every piece a different id. Anything that
      // has to look the same before and after an undo — a pawn's helmet —
      // keys off this instead.
      this.board[i] = { type, color, id: newId(type, color), origin: i };
      i++;
    }

    this.turn = turn === 'b' ? 'b' : 'w';
    this.castling = {
      wK: castling.includes('K'),
      wQ: castling.includes('Q'),
      bK: castling.includes('k'),
      bQ: castling.includes('q')
    };
    this.epSquare = ep && ep !== '-' ? this.parseSquare(ep) : null;
    this.halfmoves = Number(half ?? 0);
    this.fullmoves = Number(full ?? 1);
    this.history = [];
  }

  parseSquare(name) {
    const f = FILES.indexOf(name[0]);
    const r = Number(name[1]);
    return f < 0 || !r ? null : idx(f, r);
  }

  clone() {
    const c = Object.create(Chess.prototype);
    c.board = this.board.slice();
    c.turn = this.turn;
    c.castling = { ...this.castling };
    c.epSquare = this.epSquare;
    c.halfmoves = this.halfmoves;
    c.fullmoves = this.fullmoves;
    c.history = [];
    return c;
  }

  get(i) {
    return this.board[i];
  }

  kingSquare(color) {
    for (let i = 0; i < 64; i++) {
      const p = this.board[i];
      if (p && p.type === 'k' && p.color === color) return i;
    }
    return -1;
  }

  /** Is `square` attacked by any piece of `by`? Ignores pins and turn order. */
  isAttacked(square, by) {
    const f = fileOf(square);
    const r = rankOf(square);

    // Pawns attack diagonally forward from their own perspective.
    const pawnRank = by === 'w' ? r - 1 : r + 1;
    for (const df of [-1, 1]) {
      if (!onBoard(f + df, pawnRank)) continue;
      const p = this.board[idx(f + df, pawnRank)];
      if (p && p.color === by && p.type === 'p') return true;
    }

    for (const [df, dr] of KNIGHT_DELTAS) {
      if (!onBoard(f + df, r + dr)) continue;
      const p = this.board[idx(f + df, r + dr)];
      if (p && p.color === by && p.type === 'n') return true;
    }

    for (const [df, dr] of KING_DELTAS) {
      if (!onBoard(f + df, r + dr)) continue;
      const p = this.board[idx(f + df, r + dr)];
      if (p && p.color === by && p.type === 'k') return true;
    }

    const slide = (dirs, types) => {
      for (const [df, dr] of dirs) {
        let cf = f + df;
        let cr = r + dr;
        while (onBoard(cf, cr)) {
          const p = this.board[idx(cf, cr)];
          if (p) {
            if (p.color === by && types.includes(p.type)) return true;
            break;
          }
          cf += df;
          cr += dr;
        }
      }
      return false;
    };

    return slide(ROOK_DIRS, ['r', 'q']) || slide(BISHOP_DIRS, ['b', 'q']);
  }

  inCheck(color = this.turn) {
    const k = this.kingSquare(color);
    return k >= 0 && this.isAttacked(k, other(color));
  }

  /** Pseudo-legal moves for the side to move (may leave own king in check). */
  pseudoMoves(color = this.turn) {
    const moves = [];
    const push = (from, to, extra = {}) => {
      const captured = this.board[to];
      moves.push({ from, to, piece: this.board[from], captured, ...extra });
    };

    for (let from = 0; from < 64; from++) {
      const piece = this.board[from];
      if (!piece || piece.color !== color) continue;
      const f = fileOf(from);
      const r = rankOf(from);

      if (piece.type === 'p') {
        const dir = color === 'w' ? 1 : -1;
        const startRank = color === 'w' ? 2 : 7;
        const promoRank = color === 'w' ? 8 : 1;

        const oneR = r + dir;
        if (onBoard(f, oneR) && !this.board[idx(f, oneR)]) {
          const to = idx(f, oneR);
          if (oneR === promoRank) {
            for (const promo of ['q', 'r', 'b', 'n']) push(from, to, { promotion: promo });
          } else {
            push(from, to);
            const twoR = r + dir * 2;
            if (r === startRank && !this.board[idx(f, twoR)]) {
              push(from, idx(f, twoR), { double: true });
            }
          }
        }

        for (const df of [-1, 1]) {
          const cf = f + df;
          if (!onBoard(cf, oneR)) continue;
          const to = idx(cf, oneR);
          const target = this.board[to];
          if (target && target.color !== color) {
            if (oneR === promoRank) {
              for (const promo of ['q', 'r', 'b', 'n']) push(from, to, { promotion: promo });
            } else {
              push(from, to);
            }
          } else if (!target && to === this.epSquare) {
            const capturedSquare = idx(cf, r);
            moves.push({
              from,
              to,
              piece,
              captured: this.board[capturedSquare],
              enPassant: true,
              capturedSquare
            });
          }
        }
        continue;
      }

      if (piece.type === 'n' || piece.type === 'k') {
        const deltas = piece.type === 'n' ? KNIGHT_DELTAS : KING_DELTAS;
        for (const [df, dr] of deltas) {
          if (!onBoard(f + df, r + dr)) continue;
          const to = idx(f + df, r + dr);
          const target = this.board[to];
          if (!target || target.color !== color) push(from, to);
        }
        if (piece.type === 'k') this.addCastles(color, from, moves);
        continue;
      }

      const dirs =
        piece.type === 'r' ? ROOK_DIRS
        : piece.type === 'b' ? BISHOP_DIRS
        : ROOK_DIRS.concat(BISHOP_DIRS);
      for (const [df, dr] of dirs) {
        let cf = f + df;
        let cr = r + dr;
        while (onBoard(cf, cr)) {
          const to = idx(cf, cr);
          const target = this.board[to];
          if (!target) {
            push(from, to);
          } else {
            if (target.color !== color) push(from, to);
            break;
          }
          cf += df;
          cr += dr;
        }
      }
    }

    return moves;
  }

  addCastles(color, kingFrom, moves) {
    const rank = color === 'w' ? 1 : 8;
    if (kingFrom !== idx(4, rank)) return;
    if (this.inCheck(color)) return;
    const enemy = other(color);

    const kingSide = color === 'w' ? this.castling.wK : this.castling.bK;
    if (kingSide) {
      const empty = [idx(5, rank), idx(6, rank)];
      const rook = this.board[idx(7, rank)];
      if (
        empty.every((s) => !this.board[s]) &&
        empty.every((s) => !this.isAttacked(s, enemy)) &&
        rook && rook.type === 'r' && rook.color === color
      ) {
        moves.push({
          from: kingFrom,
          to: idx(6, rank),
          piece: this.board[kingFrom],
          captured: null,
          castle: 'K',
          rookFrom: idx(7, rank),
          rookTo: idx(5, rank)
        });
      }
    }

    const queenSide = color === 'w' ? this.castling.wQ : this.castling.bQ;
    if (queenSide) {
      const empty = [idx(1, rank), idx(2, rank), idx(3, rank)];
      const rook = this.board[idx(0, rank)];
      if (
        empty.every((s) => !this.board[s]) &&
        [idx(2, rank), idx(3, rank)].every((s) => !this.isAttacked(s, enemy)) &&
        rook && rook.type === 'r' && rook.color === color
      ) {
        moves.push({
          from: kingFrom,
          to: idx(2, rank),
          piece: this.board[kingFrom],
          captured: null,
          castle: 'Q',
          rookFrom: idx(0, rank),
          rookTo: idx(3, rank)
        });
      }
    }
  }

  /** Fully legal moves for the side to move. */
  legalMoves(color = this.turn) {
    const legal = [];
    for (const move of this.pseudoMoves(color)) {
      const undo = this.applyMove(move);
      if (!this.inCheck(color)) legal.push(move);
      this.undoMove(undo);
    }
    return legal;
  }

  movesFrom(square) {
    return this.legalMoves().filter((m) => m.from === square);
  }

  /** Applies a move without legality checking. Returns an undo record. */
  applyMove(move) {
    const undo = {
      move,
      castling: { ...this.castling },
      epSquare: this.epSquare,
      halfmoves: this.halfmoves,
      fullmoves: this.fullmoves,
      turn: this.turn,
      capturedSquare: move.enPassant ? move.capturedSquare : move.to,
      captured: move.captured ?? null
    };

    const piece = this.board[move.from];
    this.board[move.from] = null;
    if (move.enPassant) this.board[move.capturedSquare] = null;
    this.board[move.to] = move.promotion
      ? { type: move.promotion, color: piece.color, id: piece.id }
      : piece;

    if (move.castle) {
      this.board[move.rookTo] = this.board[move.rookFrom];
      this.board[move.rookFrom] = null;
    }

    this.epSquare = move.double
      ? idx(fileOf(move.from), (rankOf(move.from) + rankOf(move.to)) / 2)
      : null;

    if (piece.type === 'k') {
      if (piece.color === 'w') { this.castling.wK = false; this.castling.wQ = false; }
      else { this.castling.bK = false; this.castling.bQ = false; }
    }
    const clearRook = (square) => {
      if (square === idx(0, 1)) this.castling.wQ = false;
      if (square === idx(7, 1)) this.castling.wK = false;
      if (square === idx(0, 8)) this.castling.bQ = false;
      if (square === idx(7, 8)) this.castling.bK = false;
    };
    clearRook(move.from);
    clearRook(move.to);

    this.halfmoves = piece.type === 'p' || undo.captured ? 0 : this.halfmoves + 1;
    if (this.turn === 'b') this.fullmoves++;
    this.turn = other(this.turn);
    return undo;
  }

  undoMove(undo) {
    const move = undo.move;
    const piece = this.board[move.to];
    this.board[move.from] = move.promotion
      ? { type: 'p', color: piece.color, id: piece.id }
      : piece;
    this.board[move.to] = null;
    if (undo.captured) this.board[undo.capturedSquare] = undo.captured;
    if (move.castle) {
      this.board[move.rookFrom] = this.board[move.rookTo];
      this.board[move.rookTo] = null;
    }
    this.castling = undo.castling;
    this.epSquare = undo.epSquare;
    this.halfmoves = undo.halfmoves;
    this.fullmoves = undo.fullmoves;
    this.turn = undo.turn;
  }

  /** Plays a legal move and records it in history. */
  move(m) {
    const notation = this.toSAN(m);
    this.applyMove(m);
    this.history.push({ ...m, san: notation });
    return notation;
  }

  toSAN(move) {
    if (move.castle) return move.castle === 'K' ? 'O-O' : 'O-O-O';
    const piece = this.board[move.from];
    const target = squareName(move.to);
    let san;

    if (piece.type === 'p') {
      san = move.captured || move.enPassant
        ? `${FILES[fileOf(move.from)]}x${target}`
        : target;
      if (move.promotion) san += `=${move.promotion.toUpperCase()}`;
    } else {
      // Disambiguate against other same-type pieces reaching the same square.
      const rivals = this.legalMoves(piece.color).filter(
        (m) => m.to === move.to && m.from !== move.from &&
          this.board[m.from]?.type === piece.type
      );
      let hint = '';
      if (rivals.length) {
        const sameFile = rivals.some((m) => fileOf(m.from) === fileOf(move.from));
        const sameRank = rivals.some((m) => rankOf(m.from) === rankOf(move.from));
        hint = sameFile && sameRank ? squareName(move.from)
          : sameFile ? String(rankOf(move.from))
          : FILES[fileOf(move.from)];
      }
      san = piece.type.toUpperCase() + hint + (move.captured ? 'x' : '') + target;
    }

    const undo = this.applyMove(move);
    if (this.inCheck(this.turn)) {
      san += this.legalMoves(this.turn).length ? '+' : '#';
    }
    this.undoMove(undo);
    return san;
  }

  /** 'checkmate' | 'stalemate' | 'fifty-move' | 'insufficient' | null */
  status() {
    if (!this.legalMoves().length) {
      return this.inCheck() ? 'checkmate' : 'stalemate';
    }
    if (this.halfmoves >= 100) return 'fifty-move';
    if (this.insufficientMaterial()) return 'insufficient';
    return null;
  }

  insufficientMaterial() {
    const pieces = this.board.filter(Boolean);
    if (pieces.length > 4) return false;
    const minor = pieces.filter((p) => p.type === 'n' || p.type === 'b');
    const others = pieces.filter((p) => p.type !== 'k' && p.type !== 'n' && p.type !== 'b');
    return others.length === 0 && minor.length <= 2;
  }

  fen() {
    let placement = '';
    for (let r = 0; r < 8; r++) {
      let empty = 0;
      for (let f = 0; f < 8; f++) {
        const p = this.board[r * 8 + f];
        if (!p) { empty++; continue; }
        if (empty) { placement += empty; empty = 0; }
        placement += p.color === 'w' ? p.type.toUpperCase() : p.type;
      }
      if (empty) placement += empty;
      if (r < 7) placement += '/';
    }
    const rights =
      (this.castling.wK ? 'K' : '') + (this.castling.wQ ? 'Q' : '') +
      (this.castling.bK ? 'k' : '') + (this.castling.bQ ? 'q' : '');
    return [
      placement,
      this.turn,
      rights || '-',
      this.epSquare == null ? '-' : squareName(this.epSquare),
      this.halfmoves,
      this.fullmoves
    ].join(' ');
  }
}
